import React from 'react';
import { WithStyles } from '@material-ui/styles';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import SearchBar from '../SearchBar';
import Table from '../../components/CustomTable';
import TablePagination from '../../components/CustomTablePagination';
import styles from './styles';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants/table';
import {
  communityHeaderRows,
  getOpportunitySummaryItems,
  propertyHeaderRows,
} from './constants';
import { useOpportunityReport } from './hooks';
import {
  OpportunityCommunityTableRow,
  OpportunityPropertyTableRow,
  OpportunityTabKey,
} from './types';

type Props = WithStyles<typeof styles>;

const ReportOpportunity: React.FC<Props> = ({ classes }) => {
  const {
    activeTab,
    builderId,
    communityTable,
    error,
    handleExportAll,
    handleExportSelected,
    isBuilderSelectionRequired,
    isReportEmpty,
    loading,
    propertyTable,
    refreshReport,
    search,
    setActiveTab,
    setSearch,
    summary,
  } = useOpportunityReport();

  const activeTable = activeTab === 'communities' ? communityTable : propertyTable;
  const selectedLength = activeTable.selected.length;

  return (
    <div className={classes.pageWrapper}>
      <Grid
        container
        justify="flex-end"
        alignItems="center"
        className={classes.topActions}
      >
        <Grid item>
          <div className={classes.toolbarActions}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={refreshReport}
              disabled={!builderId || loading}
            >
              Refresh
            </Button>
          </div>
        </Grid>
      </Grid>

      <Grid container spacing={2} className={classes.summaryGrid}>
        {getOpportunitySummaryItems(summary).map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Card elevation={2} className={classes.summaryCard}>
              <CardContent className={classes.summaryCardContent}>
                <div className={classes.summaryHeader}>
                  <span className={classes.summaryValue}>{item.value}</span>
                  <span className={classes.summaryLabel}>{item.label}</span>
                </div>
                <span className={classes.summaryCaption}>{item.caption}</span>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <div className={classes.searchRow}>
        <div className={classes.searchField}>
          <SearchBar
            key={builderId || 'opportunity-search'}
            defaultValue={search}
            placeholder="Search opportunity report"
            onChange={setSearch}
          />
        </div>
      </div>

      <div className={classes.contentWrapper}>
        <Toolbar className={classes.toolbar}>
          <Typography variant="h5">
            {activeTab === 'communities' ? 'Communities' : 'Properties'}
            {selectedLength > 0 ? ` ${selectedLength} selected.` : ''}
          </Typography>
          <div className={classes.spacer} />
          {selectedLength > 0 && (
            <div className={classes.toolbarActions}>
              <span>Selected actions:</span>
              <Button
                size="small"
                disabled={selectedLength === 0}
                onClick={handleExportSelected}
              >
                Export
              </Button>
            </div>
          )}
          <Button
            size="small"
            onClick={handleExportAll}
            disabled={loading || activeTable.sorted.length === 0}
          >
            Export All
          </Button>
          {loading && <CircularProgress size={22} />}
        </Toolbar>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as OpportunityTabKey)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsRoot}
        >
          <Tab
            value="communities"
            label={`Communities (${communityTable.rows.length})`}
          />
          <Tab
            value="properties"
            label={`Properties (${propertyTable.rows.length})`}
          />
        </Tabs>

        <div className={classes.tableWrapper}>
          {loading && (
            <div className={classes.spinnerWrapper}>
              <CircularProgress />
            </div>
          )}

          {error && (
            <div className={classes.errorState}>
              <Typography variant="h6">
                Unable to load Opportunity Report
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </div>
          )}

          {!error && isBuilderSelectionRequired && (
            <div className={classes.emptyState}>
              <Typography variant="h6">Select a builder to load Opportunity Report</Typography>
              <Typography variant="body2">
                Choose a builder from the top toolbar to view this report.
              </Typography>
            </div>
          )}

          {!error && !isBuilderSelectionRequired && isReportEmpty && (
            <div className={classes.emptyState}>
              <Typography variant="h6">No feed data found</Typography>
              <Typography variant="body2">
                This builder does not have previewable feed communities or spec
                homes yet.
              </Typography>
            </div>
          )}

          {!error && !isReportEmpty && !loading && (
            activeTable.sorted.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="h6">
                  {activeTab === 'communities'
                    ? 'No communities match the current search'
                    : 'No properties match the current search'}
                </Typography>
              </div>
            ) : activeTab === 'communities' ? (
              <Table<OpportunityCommunityTableRow>
                className={classes.table}
                IDKey="rowId"
                selected={communityTable.selected}
                data={communityTable.paginated}
                rowsPerPage={communityTable.rowsPerPage}
                order={communityTable.order}
                orderBy={communityTable.orderBy}
                header={communityHeaderRows}
                onSelect={(selected) => communityTable.setSelected(selected as string[])}
                onChangeOrder={communityTable.changeOrder}
              />
            ) : (
              <Table<OpportunityPropertyTableRow>
                className={classes.table}
                IDKey="rowId"
                selected={propertyTable.selected}
                data={propertyTable.paginated}
                rowsPerPage={propertyTable.rowsPerPage}
                order={propertyTable.order}
                orderBy={propertyTable.orderBy}
                header={propertyHeaderRows}
                onSelect={(selected) => propertyTable.setSelected(selected as string[])}
                onChangeOrder={propertyTable.changeOrder}
              />
            )
          )}
        </div>

        {!error && !isBuilderSelectionRequired && !isReportEmpty && (
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            rowsPerPage={activeTable.rowsPerPage}
            currentPage={activeTable.page}
            rowsCount={activeTable.paginated.length}
            rowsTotal={activeTable.sorted.length}
            onChangePage={(page) => activeTable.setPage(page)}
            onChangeRowsPerPage={activeTable.changeRowsPerPage}
          />
        )}
      </div>
    </div>
  );
};

export default ReportOpportunity;
